import AppErrorBoundary from './AppErrorBoundary'
import { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { ChevronDown, LogOut, Menu, Search, UserRound, X } from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { usePermissionsStore } from '../store/permissionsStore'
import { getRoleLabel } from '../utils/roles'

/** Název role přihlášeného: z oprávnění (vlastní role firmy), jinak podle starého klíče. */
const useRoleName = (role: string) => usePermissionsStore((s) => s.roleName) ?? getRoleLabel(role)
import { useDismissOnOutside } from '../lib/dropdown'
import { Avatar } from './ui/Avatar'
import { Badge } from './ui/Badge'
import { Spinner } from './ui/Spinner'
import { NotificationBell } from './notifications/NotificationBell'
import { GlobalSearch } from './GlobalSearch'
import { findActiveItem, isItemActive, visibleNav, type NavItem, type NavSection } from './navigation'
import NavBadge from './NavBadge'

/** Krátké popisky sekcí pod ikonou v úzkém pruhu (tablet) — max. ~8 znaků, aby se vešly na 80 px. */
const SHORT_LABELS: Record<string, string> = {
  overview: 'Přehled',
  attendance: 'Docházka',
  orders: 'Zakázky',
  preparations: 'Přípravy',
  tools: 'Nářadí',
  tasks: 'Úkoly',
  revisions: 'Revize',
  reports: 'Reporty',
  admin: 'Správa',
}

/**
 * Hlavní layout aplikace — JEDNO boční menu pro všechny moduly.
 *
 * - Mobil (< lg): horní lišta (menu, logo, název stránky, profil) + vysouvací panel s celým menu.
 * - Tablet (lg): úzký pruh 80 px s ikonami a krátkými popisky; sekce s více položkami otevře
 *   po ťuknutí nabídku podstránek (nic není jen na hover).
 * - Desktop (xl+): plné menu 256 px se sbalitelnými sekcemi, profil a odhlášení dole.
 *
 * Výška = viditelná plocha minus pruh impersonace (`--impersonation-bar`, viz ImpersonationBanner).
 * Obsah stránky se posouvá v <main>, menu zůstává na místě.
 */
export default function AppLayout() {
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  // Odběr oprávnění — menu se přepočítá po načtení /auth/permissions.
  const capabilities = usePermissionsStore((s) => s.capabilities)
  const isAdmin = usePermissionsStore((s) => s.isAdmin)
  const hidden = usePermissionsStore((s) => s.hiddenFeatures)
  const sections = useMemo(() => visibleNav(user), [user, capabilities, isAdmin, hidden]) // eslint-disable-line react-hooks/exhaustive-deps
  const active = findActiveItem(sections, pathname)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const mainRef = useRef<HTMLElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const mobileSearchRef = useRef<HTMLInputElement>(null)
  const [mobileSearch, setMobileSearch] = useState(false)
  // Skryté boční menu na desktopu/tabletu (hamburger v horní liště, vzor Serviqo) — pamatuje si prohlížeč.
  const [navHidden, setNavHidden] = useState(() => { try { return localStorage.getItem('das.navHidden') === '1' } catch { return false } })
  const toggleNav = () => setNavHidden((h) => {
    try { localStorage.setItem('das.navHidden', h ? '0' : '1') } catch { /* soukromý režim */ }
    return !h
  })
  // Otevřené hledání na telefonu: kurzor rovnou do pole.
  useEffect(() => { if (mobileSearch) mobileSearchRef.current?.focus() }, [mobileSearch])
  useEffect(() => { setMobileSearch(false) }, [pathname])
  // Ctrl+K / ⌘K zaostří hledání odkudkoli.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        if (window.matchMedia('(min-width: 1024px)').matches) searchRef.current?.focus()
        else setMobileSearch(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  // Po přechodu na jinou stránku zavřít mobilní menu a posunout obsah nahoru.
  useEffect(() => {
    setDrawerOpen(false)
    mainRef.current?.scrollTo({ top: 0 })
  }, [pathname])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const pageTitle = active?.item.label
    ?? (pathname === '/profile' ? 'Můj profil' : pathname.startsWith('/notifications') ? 'Notifikace' : 'DaS Flow')

  return (
    <div className="flex h-app overflow-hidden bg-slate-50">
      {/* Tablet: ikonový pruh */}
      <aside className={clsx('hidden w-20 shrink-0 flex-col border-r border-slate-200 bg-white xl:hidden', !navHidden && 'lg:flex')}>
        <RailNav sections={sections} pathname={pathname} onLogout={handleLogout} />
      </aside>

      {/* Desktop: plné menu */}
      <aside className={clsx('hidden w-64 shrink-0 flex-col border-r border-slate-200 bg-white', !navHidden && 'xl:flex')}>
        <FullNav sections={sections} pathname={pathname} onLogout={handleLogout} showProfile={false} />
      </aside>

      {/* Mobil: vysouvací panel */}
      {drawerOpen && <MobileDrawer sections={sections} pathname={pathname} onLogout={handleLogout} onClose={() => setDrawerOpen(false)} />}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Horní lišta (vzor Serviqo): název stránky, fulltext, notifikace a profil. */}
        <header className="z-30 hidden shrink-0 items-center gap-4 border-b border-slate-200/70 bg-white/80 px-6 py-2.5 backdrop-blur lg:flex">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <button
              type="button"
              onClick={toggleNav}
              aria-label={navHidden ? 'Zobrazit menu' : 'Skrýt menu'}
              aria-pressed={!navHidden}
              title={navHidden ? 'Zobrazit menu' : 'Skrýt menu'}
              className="-ml-2 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-ink-500 transition hover:bg-slate-100 hover:text-ink-900"
            >
              <Menu size={20} />
            </button>
            {navHidden && (
              <Link to="/" aria-label="Úvodní stránka" className="shrink-0"><img src="/das-mark.png" alt="" className="h-8 w-8" /></Link>
            )}
            <p className="min-w-0 truncate text-[15px] font-bold text-ink-900">{pageTitle}</p>
          </div>
          <div className="w-full max-w-md flex-[2]">
            <GlobalSearch ref={searchRef} />
          </div>
          <div className="flex flex-1 shrink-0 items-center justify-end gap-1">
            <NotificationBell variant="desktop" />
            <ProfileMenu onLogout={handleLogout} placement="down" withName />
          </div>
        </header>

        {mobileSearch && (
          <div className="pt-safe fixed inset-x-0 top-0 z-[85] flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2.5 shadow-md lg:hidden">
            <GlobalSearch ref={mobileSearchRef} onNavigate={() => setMobileSearch(false)} />
            <button type="button" onClick={() => setMobileSearch(false)} className="min-h-[44px] shrink-0 px-2 text-sm font-medium text-ink-700">Zavřít</button>
          </div>
        )}

        <header className="pt-safe z-30 shrink-0 border-b border-slate-200 bg-white lg:hidden">
          <div className="flex h-14 items-center gap-1 px-1.5">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Otevřít menu"
              aria-expanded={drawerOpen}
              className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-ink-700 hover:bg-slate-100"
            >
              <Menu size={22} />
            </button>
            <Link to="/" aria-label="Úvodní stránka" className="inline-flex h-11 shrink-0 items-center px-1">
              <img src="/das-mark.png" alt="" className="h-8 w-8" />
            </Link>
            <h1 className="min-w-0 flex-1 truncate px-1.5 text-base font-semibold text-ink-900">{pageTitle}</h1>
            <button type="button" onClick={() => setMobileSearch(true)} aria-label="Hledat" className="inline-flex h-11 w-11 items-center justify-center rounded-lg text-ink-700 hover:bg-slate-100">
              <Search size={22} />
            </button>
            <NotificationBell variant="mobile" />
            <ProfileMenu onLogout={handleLogout} placement="down" />
          </div>
        </header>

        <main ref={mainRef} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          <div className="w-full px-4 pb-8 pt-4 sm:px-6 sm:pt-6 lg:px-8">
            {/* Stránky se načítají po částech — menu a horní lišta zůstanou, jen obsah ukáže načítání. */}
            <AppErrorBoundary resetKey={pathname}>
              <Suspense fallback={<div className="flex justify-center py-16"><Spinner /></div>}>
                <Outlet />
              </Suspense>
            </AppErrorBoundary>
          </div>
        </main>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Plné menu (desktop + mobilní panel)
// ---------------------------------------------------------------------------------------------

function NavLinkItem({ item, pathname, onNavigate, nested }: { item: NavItem; pathname: string; onNavigate?: () => void; nested?: boolean }) {
  const isActive = isItemActive(item, pathname)
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      className={clsx(
        'relative flex min-h-[44px] items-center gap-3 rounded-lg pr-3 text-sm transition-colors',
        nested ? 'pl-9' : 'pl-3',
        isActive
          ? 'bg-brand-50 font-semibold text-brand-700 before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-brand-600'
          : 'font-medium text-ink-700 hover:bg-slate-100 hover:text-ink-900',
      )}
    >
      {!nested && <Icon size={18} className={clsx('shrink-0', isActive ? 'text-brand-600' : 'text-ink-500')} />}
      <span className="truncate">{item.label}</span>
      {item.badge && <NavBadge kind={item.badge} />}
    </Link>
  )
}

function FullNav({ sections, pathname, onLogout, onNavigate, showProfile = true }: { sections: NavSection[]; pathname: string; onLogout: () => void; onNavigate?: () => void; showProfile?: boolean }) {
  // Akordeon: rozbalená je jen jedna skupina — ta s aktuální stránkou, nebo ta, kterou uživatel rozklikl.
  const activeId = sections.find((s) => s.items.length > 1 && s.items.some((i) => isItemActive(i, pathname)))?.id ?? null
  const [openId, setOpenId] = useState<string | null>(activeId)
  useEffect(() => { setOpenId(activeId) }, [activeId])
  const testInstance = usePermissionsStore((s) => s.testInstance)
  const toggle = (id: string) => setOpenId((cur) => (cur === id ? null : id))

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-20 shrink-0 items-center justify-center gap-2 border-b border-slate-100 px-4">
        <Link to="/" onClick={onNavigate} aria-label="DaS elektromontáže — úvodní stránka" className="flex items-center gap-2">
          <img src="/das-logo.png" alt="DaS elektromontáže" className="h-11 w-auto" />
          {testInstance && <Badge tone="warning">TEST</Badge>}
        </Link>
      </div>

      <nav className="scrollbar-thin min-h-0 flex-1 space-y-1 overflow-y-auto px-3 pb-4 pt-3" aria-label="Hlavní menu">
        {sections.map((section) => {
          // Sekce s jedinou položkou se stejným názvem = rovnou odkaz (Přehled, Revize).
          if (section.items.length === 1) {
            return <NavLinkItem key={section.id} item={{ ...section.items[0], icon: section.icon }} pathname={pathname} onNavigate={onNavigate} />
          }
          const containsActive = section.items.some((i) => isItemActive(i, pathname))
          const isOpen = openId === section.id
          const Icon = section.icon
          return (
            <div key={section.id} className="pt-1">
              <button
                type="button"
                onClick={() => toggle(section.id)}
                aria-expanded={isOpen}
                className={clsx(
                  'flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 text-left text-sm font-semibold transition-colors hover:bg-slate-100',
                  containsActive ? 'text-ink-900' : 'text-ink-700',
                )}
              >
                <Icon size={18} className={clsx('shrink-0', containsActive ? 'text-brand-600' : 'text-ink-500')} />
                <span className="flex-1 truncate">{section.label}</span>
                <ChevronDown size={16} className={clsx('shrink-0 text-ink-400 transition-transform', isOpen ? 'rotate-0' : '-rotate-90')} />
              </button>
              {isOpen && (
                <div className="mt-0.5 space-y-0.5">
                  {section.items.map((item) => (
                    <NavLinkItem key={item.to} item={item} pathname={pathname} onNavigate={onNavigate} nested />
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {showProfile && <ProfileBlock onLogout={onLogout} onNavigate={onNavigate} />}
    </div>
  )
}

function ProfileBlock({ onLogout, onNavigate }: { onLogout: () => void; onNavigate?: () => void }) {
  const user = useAuthStore((s) => s.user)
  const roleName = useRoleName(user?.role ?? '')
  if (!user) return null
  return (
    <div className="shrink-0 border-t border-slate-200 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <Link
        to="/profile"
        onClick={onNavigate}
        aria-label={`Můj profil: ${user.name}`}
        className="flex min-h-[44px] items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-slate-100"
      >
        <Avatar name={user.name} initials={user.initials} color={user.color} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-ink-900">{user.name}</p>
          <p className="truncate text-xs text-ink-500">{roleName} · {user.tenant.name}</p>
        </div>
      </Link>
      <div className="mt-1">
        <button
          type="button"
          onClick={onLogout}
          className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg text-sm font-medium text-ink-700 hover:bg-red-50 hover:text-red-700"
        >
          <LogOut size={16} /> Odhlásit
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Mobilní vysouvací panel
// ---------------------------------------------------------------------------------------------

function MobileDrawer({ sections, pathname, onLogout, onClose }: { sections: NavSection[]; pathname: string; onLogout: () => void; onClose: () => void }) {
  // Zavření klikem na pozadí jen když stisk i puštění proběhly na pozadí (ne tah z panelu ven).
  const downOnBackdrop = useRef(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-x-0 bottom-0 top-[var(--impersonation-bar)] z-[80] animate-backdrop-in bg-slate-900/50 lg:hidden"
      onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget }}
      onClick={(e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) onClose()
        downOnBackdrop.current = false
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
        className="pt-safe relative flex h-full w-[86%] max-w-xs animate-drawer-in flex-col bg-white shadow-soft-lg"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Zavřít menu"
          className="absolute right-2 top-[calc(env(safe-area-inset-top)+1.125rem)] z-10 inline-flex h-11 w-11 items-center justify-center rounded-lg text-ink-600 hover:bg-slate-100"
        >
          <X size={22} />
        </button>
        <FullNav sections={sections} pathname={pathname} onLogout={onLogout} onNavigate={onClose} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------------------------
// Tablet: ikonový pruh s nabídkou podstránek
// ---------------------------------------------------------------------------------------------

function RailNav({ sections, pathname }: { sections: NavSection[]; pathname: string; onLogout?: () => void }) {
  const [openId, setOpenId] = useState<string | null>(null)
  const [anchorTop, setAnchorTop] = useState(0)
  const flyoutRef = useRef<HTMLDivElement>(null)
  const railRef = useDismissOnOutside<HTMLDivElement>(openId !== null, () => setOpenId(null), [flyoutRef])

  useEffect(() => { setOpenId(null) }, [pathname])

  const openSection = sections.find((s) => s.id === openId) ?? null

  return (
    <div ref={railRef} className="flex h-full min-h-0 flex-col items-center">
      <Link to="/" aria-label="DaS elektromontáže — úvodní stránka" className="flex h-16 shrink-0 items-center justify-center">
        <img src="/das-mark.png" alt="" className="h-9 w-9" />
      </Link>
      <nav className="scrollbar-thin flex min-h-0 w-full flex-1 flex-col items-center gap-1 overflow-y-auto px-1.5 pb-3" aria-label="Hlavní menu">
        {sections.map((section) => {
          const Icon = section.icon
          const containsActive = section.items.some((i) => isItemActive(i, pathname))
          const label = SHORT_LABELS[section.id] ?? section.label
          const cls = clsx(
            'flex w-full flex-col items-center justify-center gap-1 rounded-lg px-0.5 py-2 text-xs font-medium transition-colors',
            containsActive ? 'bg-brand-50 text-brand-700' : openId === section.id ? 'bg-slate-100 text-ink-900' : 'text-ink-600 hover:bg-slate-100 hover:text-ink-900',
          )
          if (section.items.length === 1) {
            return (
              <Link key={section.id} to={section.items[0].to} aria-label={section.label} aria-current={containsActive ? 'page' : undefined} className={cls}>
                <Icon size={20} />
                <span className="w-full truncate text-center">{label}</span>
              </Link>
            )
          }
          return (
            <button
              key={section.id}
              type="button"
              aria-label={section.label}
              aria-expanded={openId === section.id}
              aria-haspopup="menu"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect()
                setAnchorTop(r.top)
                setOpenId((cur) => (cur === section.id ? null : section.id))
              }}
              className={cls}
            >
              <Icon size={20} />
              <span className="w-full truncate text-center">{label}</span>
            </button>
          )
        })}
      </nav>
      <div className="shrink-0 border-t border-slate-200 py-2">

      </div>

      {openSection && (
        <RailFlyout panelRef={flyoutRef} section={openSection} pathname={pathname} top={anchorTop} />
      )}
    </div>
  )
}

function RailFlyout({ section, pathname, top, panelRef: ref }: { section: NavSection; pathname: string; top: number; panelRef: React.RefObject<HTMLDivElement> }) {
  const [pos, setPos] = useState(top)
  useLayoutEffect(() => {
    const h = ref.current?.offsetHeight ?? 0
    const max = window.innerHeight - h - 8
    setPos(Math.max(8, Math.min(top, max)))
  }, [top, ref, section.id])
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={section.label}
      className="fixed left-[84px] z-[70] w-64 animate-fade-in rounded-xl border border-slate-200 bg-white p-2 shadow-soft-lg"
      style={{ top: pos }}
    >
      <p className="px-3 pb-1 pt-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500">{section.label}</p>
      {section.items.map((item) => (
        <NavLinkItem key={item.to} item={item} pathname={pathname} />
      ))}
    </div>,
    document.body,
  )
}

// ---------------------------------------------------------------------------------------------
// Profil (mobilní lišta, tablet)
// ---------------------------------------------------------------------------------------------

function ProfileMenu({ onLogout, placement, withName = false }: { onLogout: () => void; placement: 'down' | 'right'; withName?: boolean }) {
  const user = useAuthStore((s) => s.user)
  const roleName = useRoleName(user?.role ?? '')
  const [open, setOpen] = useState(false)
  const ref = useDismissOnOutside<HTMLDivElement>(open, () => setOpen(false))
  const { pathname } = useLocation()
  useEffect(() => { setOpen(false) }, [pathname])
  if (!user) return null
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Profil: ${user.name}`}
        aria-expanded={open}
        aria-haspopup="menu"
        className={clsx('inline-flex h-11 items-center justify-center hover:bg-slate-100', withName ? 'gap-2.5 rounded-full py-1 pl-1 pr-3' : 'w-11 rounded-full')}
      >
        <Avatar name={user.name} initials={user.initials} color={user.color} size="sm" />
        {withName && (
          <span className="hidden min-w-0 text-left xl:block">
            <span className="block max-w-[160px] truncate text-sm font-semibold leading-tight text-ink-900">{user.name}</span>
            <span className="block max-w-[160px] truncate text-xs leading-tight text-ink-500">{roleName}</span>
          </span>
        )}
        {withName && <ChevronDown size={14} className="hidden shrink-0 text-ink-400 xl:block" aria-hidden />}
      </button>
      {open && (
        <div
          role="menu"
          className={clsx(
            'absolute z-[70] w-64 animate-fade-in rounded-xl border border-slate-200 bg-white p-2 shadow-soft-lg',
            placement === 'down' ? 'right-0 top-full mt-1' : 'bottom-0 left-full ml-2',
          )}
        >
          <Link to="/profile" role="menuitem" className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-100">
            <Avatar name={user.name} initials={user.initials} color={user.color} size="md" />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink-900">{user.name}</p>
              <p className="truncate text-xs text-ink-500">{roleName}</p>
              <p className="truncate text-xs text-ink-500">{user.tenant.name}</p>
            </div>
          </Link>
          <Link to="/profile" role="menuitem" className="flex min-h-[44px] items-center gap-3 rounded-lg px-3 text-sm font-medium text-ink-800 hover:bg-slate-100">
            <UserRound size={18} className="text-ink-500" /> Můj profil a heslo
          </Link>
          <div className="my-1 border-t border-slate-100" />
          <button
            type="button"
            role="menuitem"
            onClick={onLogout}
            className="flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 text-sm font-medium text-red-700 hover:bg-red-50"
          >
            <LogOut size={18} /> Odhlásit
          </button>
        </div>
      )}
    </div>
  )
}
